import { useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '@/lib/enhancedAxiosInstance';
import { API_ENDPOINTS } from '@/config/config';

// Global attributes cache - called once and shared across all components
export function useGlobalAttributes() {
  return useQuery({
    queryKey: ['globalAttributes'],
    queryFn: async () => {
      const { data } = await axiosInstance.get('/api/attributes/');

      // Create a lookup map for fast access
      const attrMap = Array.isArray(data)
        ? data.reduce<Record<number, { label: string; code: string; type?: string }>>(
            (acc, a: any) => {
              acc[a.id] = {
                label: a.label || a.name || '',
                code: a.code || '',
                type: a.type || a.attribute_type || '',
              };
              return acc;
            },
            {}
          )
        : {};

      return {
        attributes: data || [],
        attributeMap: attrMap,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - reduced from 15 for faster updates
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
}

// Helper hook to invalidate attributes cache after imports
export function useInvalidateAttributesCache() {
  const queryClient = useQueryClient();

  return () => {
    // Core structure caches
    queryClient.invalidateQueries({ queryKey: ['globalAttributes'] });
    queryClient.invalidateQueries({ queryKey: ['attributes'] });
    queryClient.invalidateQueries({ queryKey: ['attributeGroups'] });
    queryClient.invalidateQueries({ queryKey: ['families'] });

    // Schema and field caches
    queryClient.invalidateQueries({ queryKey: ['attribute-schema'] });
    queryClient.invalidateQueries({ queryKey: ['attribute-group-schema'] });
    queryClient.invalidateQueries({ queryKey: ['family-schema'] });

    // Specific attribute and group caches
    queryClient.invalidateQueries({ queryKey: ['allAttributes'] });
    queryClient.invalidateQueries({ queryKey: ['allAttributeGroups'] });
    queryClient.invalidateQueries({ queryKey: ['attributeStats'] });

    // Family-related caches
    queryClient.invalidateQueries({ queryKey: ['familyAttributeGroups'] });
    queryClient.invalidateQueries({ queryKey: ['family-attributes'] });

    // Product-related attribute caches
    queryClient.invalidateQueries({ queryKey: ['product-attribute-groups'] });
    queryClient.invalidateQueries({ queryKey: ['effectiveAttributesLegacy'] });
    queryClient.invalidateQueries({
      queryKey: ['effectiveAttributeGroupsLegacy'],
    });

    // Analytics and reporting caches
    queryClient.invalidateQueries({ queryKey: ['analytics'] });
    queryClient.invalidateQueries({ queryKey: ['attribute-adoption'] });
    queryClient.invalidateQueries({ queryKey: ['locale-attributes'] });
    queryClient.invalidateQueries({
      queryKey: ['dashboard', 'required-attributes'],
    });

    // Import schema caches
    queryClient.invalidateQueries({ queryKey: ['attribute-group-schema'] });
    queryClient.invalidateQueries({ queryKey: ['attribute-schema'] });
  };
}

// Specific cache invalidation for attribute groups only
export function useInvalidateAttributeGroupsCache() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['attributeGroups'] });
    queryClient.invalidateQueries({ queryKey: ['allAttributeGroups'] });
    queryClient.invalidateQueries({ queryKey: ['attribute-group-schema'] });
    queryClient.invalidateQueries({ queryKey: ['familyAttributeGroups'] });
    queryClient.invalidateQueries({ queryKey: ['product-attribute-groups'] });
    queryClient.invalidateQueries({
      queryKey: ['effectiveAttributeGroupsLegacy'],
    });
  };
}

// Specific cache invalidation for attributes only
export function useInvalidateAttributesOnlyCache() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['globalAttributes'] });
    queryClient.invalidateQueries({ queryKey: ['attributes'] });
    queryClient.invalidateQueries({ queryKey: ['allAttributes'] });
    queryClient.invalidateQueries({ queryKey: ['attributeStats'] });
    queryClient.invalidateQueries({ queryKey: ['attribute-schema'] });
    queryClient.invalidateQueries({ queryKey: ['effectiveAttributesLegacy'] });
    queryClient.invalidateQueries({
      queryKey: ['dashboard', 'required-attributes'],
    });
    queryClient.invalidateQueries({ queryKey: ['analytics'] });
    queryClient.invalidateQueries({ queryKey: ['attribute-adoption'] });
    queryClient.invalidateQueries({ queryKey: ['locale-attributes'] });
  };
}

// Specific cache invalidation for families only
export function useInvalidateFamiliesCache() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['families'] });
    queryClient.invalidateQueries({ queryKey: ['family-schema'] });
    queryClient.invalidateQueries({ queryKey: ['familyAttributeGroups'] });
    queryClient.invalidateQueries({ queryKey: ['family-attributes'] });
  };
}
